import { useMemo, useState } from 'react';
import { xor } from 'lodash';
import { GridListProps, Key } from 'react-aria-components';

import { Label } from '../../../../../../../common/labels/labelTypes';
import { usePlaySound } from '../../../../../../sounds/usePlaySound';
import { SoundId } from '../../../../../../sounds/soundConstants';

export const CREATE_LABEL = 'CREATE_LABEL';

type Props = {
    selectedLabelIds: Set<string>;
    handleCreateLabel: () => void;
    handleToggleLabelSelection: (labelId: string) => void;
};

type ReturnType = {
    checkedLabelIds: Set<Key>;
    editingLabelId: string | null;
    gridListInteractionProps: Pick<
        GridListProps<Label>,
        'selectionMode' | 'keyboardNavigationBehavior' | 'disallowTypeAhead' | 'escapeKeyBehavior'
    >;
    handleEditingChange: (labelId: string, nextIsEditing: boolean) => void;
    handleSelectionChange: (keys: 'all' | Set<Key>) => void;
};

type GridListInteractionProps = Pick<
    GridListProps<Label>,
    'selectionMode' | 'keyboardNavigationBehavior' | 'disallowTypeAhead' | 'escapeKeyBehavior'
>;

const useLabelListSelection = ({
    selectedLabelIds,
    handleCreateLabel,
    handleToggleLabelSelection,
}: Props): ReturnType => {
    const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
    const isEditingMode = Boolean(editingLabelId);

    const checkedLabelIds = useMemo<Set<Key>>(() => new Set(selectedLabelIds), [selectedLabelIds]);

    const gridListInteractionProps = useMemo<GridListInteractionProps>(
        () => ({
            selectionMode: isEditingMode ? 'none' : 'multiple',
            keyboardNavigationBehavior: isEditingMode ? 'tab' : 'arrow',
            disallowTypeAhead: isEditingMode,
            escapeKeyBehavior: 'none' as const,
        }),
        [isEditingMode],
    );

    const playSound = usePlaySound();

    const handleSelectionChange = (keys: 'all' | Set<Key>) => {
        if (isEditingMode || keys === 'all') return;

        const nextKeys = new Set(keys);

        if (nextKeys.has(CREATE_LABEL)) {
            handleCreateLabel();
            return;
        }

        nextKeys.delete(CREATE_LABEL);

        const toggledIds = xor([...nextKeys], [...checkedLabelIds]);
        if (toggledIds.length !== 1) return;

        const toggledLabelId = toggledIds[0];
        if (typeof toggledLabelId !== 'string') return;

        // The label rows render a (presentational) checkbox, so sound like one
        playSound(nextKeys.has(toggledLabelId) ? SoundId.CheckboxTick : SoundId.CheckboxUntick);
        handleToggleLabelSelection(toggledLabelId);
    };

    const handleEditingChange = (labelId: string, nextIsEditing: boolean) => {
        if (nextIsEditing) {
            setEditingLabelId(labelId);
            return;
        }

        setEditingLabelId((currentEditingLabelId) =>
            currentEditingLabelId === labelId ? null : currentEditingLabelId,
        );
    };

    return {
        checkedLabelIds,
        editingLabelId,
        gridListInteractionProps,
        handleEditingChange,
        handleSelectionChange,
    };
};

export default useLabelListSelection;
